import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../utils/toast';
import { register } from '../services/users';
import { getErrorMessages } from '../utils/errorResponse';
import LanguageSwitcher from '../components/languageSwitcher';
import './auth.css';

// Mirrors PasswordSchema in the API: length only, no composition rules.
const PASSWORD_MIN_LENGTH = 15
const PASSWORD_MAX_LENGTH = 128

const RegisterPage = () => {

    const { t } = useTranslation();
    const [user, setUser] = useState({
        fullName: "",
        username: "",
        password: "",
        email: ""
    })
    const [confirmPassword, setConfirmPassword] = useState("")

    const [error, setError] = useState()

    const navigate = useNavigate()

    const queryClient = useQueryClient()
    const usersMutation = useMutation(register, {
        onSuccess: (body) => {
            if (!body || body.code !== 200) {
                const message = (body && typeof body.message === 'string' && body.message)
                    || t('auth.registrationFailed')
                toast.error(message, { position: toast.POSITION.BOTTOM_RIGHT })
                return
            }

            queryClient.invalidateQueries('users')
            toast.success(t('auth.accountCreated'), {
                position: toast.POSITION.BOTTOM_RIGHT
            })

            const email = (body.message && body.message.email) || user.email
            navigate('/thank-you', { state: { email } })
        },
        onError: ({ response }) => {
            toast.error(getErrorMessages(response.data.responseException), {
                position: toast.POSITION.BOTTOM_RIGHT
            })
        }
    })

    const handleChangeInput = (prop) => {
        return (e) => {
            const value = e.target.value
            setUser({ ...user, [prop]: value })
        }
    }

    const handleSubmit = async (e) => {

        setError()
        e.preventDefault()
        e.stopPropagation()

        if ((user.password || '').length < PASSWORD_MIN_LENGTH) {
            setError(t('auth.passwordMinLength', { min: PASSWORD_MIN_LENGTH }))
            return
        }
        if (user.password.length > PASSWORD_MAX_LENGTH) {
            setError(t('auth.passwordMaxLength', { max: PASSWORD_MAX_LENGTH }))
            return
        }
        if (user.password !== confirmPassword) {
            setError(t('auth.passwordsDoNotMatch'))
            return
        }

        usersMutation.mutate({ ...user })
    }

    return (
        <div className="auth-page">
            <div className="auth-shell">

                {/* ───────── LEFT / BRAND PANEL ───────── */}
                <aside className="auth-brand-panel">
                    <div className="auth-brand-top">
                        <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                            <img
                                src={process.env.PUBLIC_URL + '/logo-soroby.svg'}
                                alt="Soroby"
                                className="auth-brand-logo"
                            />
                        </Link>
                    </div>

                    <div className="auth-brand-message">
                        <h1 className="auth-brand-title" dangerouslySetInnerHTML={{ __html: t('auth.brandTitle') }} />
                        <p className="auth-brand-tagline">
                            {t('auth.brandTagline')}
                        </p>
                    </div>

                    <div className="auth-brand-footer">
                        <span>© {new Date().getFullYear()} Soroby · {t('auth.builtForServiceBusinesses')}</span>
                        <span>soroby.com</span>
                    </div>
                </aside>

                {/* ───────── RIGHT / FORM PANEL ───────── */}
                <main className="auth-form-panel">
                    <LanguageSwitcher className="auth-lang" id="auth-language-drop" drop="down" align="end" showIcon />

                    <form className="auth-form-inner wide" onSubmit={handleSubmit}>

                        <div className="auth-mobile-brand">
                            <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                                <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" />
                            </Link>
                        </div>

                        <div className="auth-form-heading">
                            <h2>{t('auth.createYourWorkspace')}</h2>
                            <p>{t('auth.justAFewDetails')}</p>
                        </div>

                        <div className="auth-form">

                            <div className="auth-field">
                                <label className="auth-label" htmlFor="reg-username">{t('auth.username')}</label>
                                <div className="auth-input-wrap">
                                    <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                        <circle cx="12" cy="7" r="4" />
                                    </svg>
                                    <input
                                        id="reg-username"
                                        className="auth-input"
                                        type="text"
                                        autoComplete="username"
                                        placeholder="pick.a.handle"
                                        required
                                        onChange={handleChangeInput('username')}
                                    />
                                </div>
                            </div>

                            <div className="auth-row-2">
                                <div className="auth-field">
                                    <label className="auth-label" htmlFor="reg-password">{t('auth.password')}</label>
                                    <div className="auth-input-wrap">
                                        <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                        </svg>
                                        <input
                                            id="reg-password"
                                            className="auth-input"
                                            type="password"
                                            autoComplete="new-password"
                                            placeholder={t('auth.atLeastNCharacters', { min: PASSWORD_MIN_LENGTH })}
                                            required
                                            onChange={handleChangeInput('password')}
                                        />
                                    </div>
                                </div>
                                <div className="auth-field">
                                    <label className="auth-label" htmlFor="reg-password-2">{t('auth.confirm')}</label>
                                    <div className="auth-input-wrap">
                                        <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                        </svg>
                                        <input
                                            id="reg-password-2"
                                            className="auth-input"
                                            type="password"
                                            autoComplete="new-password"
                                            placeholder={t('auth.repeatPassword')}
                                            required
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="auth-row-2">
                                <div className="auth-field">
                                    <label className="auth-label" htmlFor="reg-fullname">{t('auth.fullName')}</label>
                                    <div className="auth-input-wrap">
                                        <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                            <circle cx="12" cy="7" r="4" />
                                        </svg>
                                        <input
                                            id="reg-fullname"
                                            className="auth-input"
                                            type="text"
                                            autoComplete="name"
                                            placeholder="Jane Reed"
                                            required
                                            onChange={handleChangeInput('fullName')}
                                        />
                                    </div>
                                </div>
                                <div className="auth-field">
                                    <label className="auth-label" htmlFor="reg-email">{t('auth.email')}</label>
                                    <div className="auth-input-wrap">
                                        <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <rect x="3" y="5" width="18" height="14" rx="2" />
                                            <path d="M3 7l9 6 9-6" />
                                        </svg>
                                        <input
                                            id="reg-email"
                                            className="auth-input"
                                            type="email"
                                            autoComplete="email"
                                            placeholder="you@company.com"
                                            required
                                            onChange={handleChangeInput('email')}
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="auth-btn"
                                disabled={usersMutation.isLoading}
                            >
                                {usersMutation.isLoading ? (
                                    <span>{t('auth.creatingWorkspace')}</span>
                                ) : (
                                    <>
                                        <span>{t('auth.createWorkspace')}</span>
                                        <span className="arrow" aria-hidden="true">→</span>
                                    </>
                                )}
                            </button>

                            {error &&
                                <div className="auth-error">{error}</div>
                            }
                        </div>

                        <p className="auth-secondary">
                            {t('auth.alreadyHaveAccount')} <Link to="/login">{t('auth.signInInstead')}</Link>
                        </p>
                    </form>
                </main>

            </div>
        </div>
    );
};

export default RegisterPage;
